'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ArrowUpRight, Loader2, Eye, EyeOff } from 'lucide-react'
import api from '@/lib/api'
import { resolvePostLoginPath } from '@/lib/company-navigation'
import { VelovaIcon, VelovaLogo } from '@/components/velova-logo'
import { SITE_META } from '@/lib/site-config'

// Editorial blocks for the left panel — mirrors the SIGNUP_STEPS treatment on
// /signup so the two auth pages read as siblings. For a returning user these
// describe what's waiting in the workspace rather than onboarding steps.
const LOGIN_HIGHLIGHTS = [
  {
    index: '01',
    title: "Today's answers",
    description: 'How AI responded to your buyer questions overnight.',
    block: 'velova-block-blue',
  },
  {
    index: '02',
    title: 'Citations & competitors',
    description: 'Who gets cited, who wins, and what moved this week.',
    block: 'velova-block-mint',
  },
  {
    index: '03',
    title: 'Prioritized actions',
    description: 'The next work to close the gaps, ready to review.',
    block: 'velova-block-amber',
  },
] as const

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  // Surface errors returned from the Google OAuth callback
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const err = params.get('error')
    if (err) {
      setError(err)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const handleGoogleSignIn = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      const response = await api.get('/auth/google/start')
      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl
      } else {
        throw new Error('No auth URL returned')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Google sign-in failed')
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await api.post('/auth/login', { email, password })
      localStorage.setItem('token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.user))

      try {
        const destination = await resolvePostLoginPath()
        router.replace(destination)
      } catch (navigationError) {
        console.error('Failed to resolve post-login destination:', navigationError)
        router.replace('/dashboard')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="old-landing velova-canvas velova-grain min-h-screen flex">
      {/* Left panel — editorial showcase */}
      <aside className="hidden lg:flex lg:w-[52%] relative overflow-hidden border-r border-[var(--velova-line-strong)]">
        <div className="absolute inset-0 velova-brush opacity-80 pointer-events-none" />

        <div className="relative w-full flex flex-col justify-between p-12 xl:p-16">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3 group">
              <VelovaLogo variant="light" />
            </Link>
            <span className="velova-mono-label opacity-60">§ Access</span>
          </div>

          {/* Middle editorial content — mirrors /signup: eyebrow, display
              headline with an italic continuation, then editorial blocks. */}
          <div className="max-w-[560px]">
            <div className="velova-mono-label opacity-60 mb-6">
              Pick up where you left off
            </div>

            <h1 className="velova-display text-[52px] xl:text-[64px] leading-[1.02]">
              The answer moved.{' '}
              <span className="velova-italic" style={{ color: 'var(--velova-blue-deep)' }}>
                See what changed.
              </span>
            </h1>

            {/* What's waiting in the workspace — editorial blocks */}
            <div className="mt-10 space-y-3 max-w-md">
              {LOGIN_HIGHLIGHTS.map((item) => (
                <div key={item.index} className={`velova-block ${item.block} flex gap-4 p-4`}>
                  <span className="velova-display text-[28px] leading-none opacity-50">
                    {item.index}
                  </span>
                  <div>
                    <div className="text-[14px] font-semibold">{item.title}</div>
                    <div className="text-[13px] opacity-70 mt-1 leading-snug">
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom byline */}
          <div className="text-[12px] opacity-60">— The Velova team</div>
        </div>
      </aside>

      {/* Right panel — form */}
      <section className="flex-1 flex flex-col">
        {/* Mobile header with logo */}
        <div className="lg:hidden p-5 sm:p-8 flex items-center justify-between border-b border-[var(--velova-line-strong)]">
          <Link href="/" className="flex items-center gap-3">
            <VelovaLogo variant="light" width={100} />
          </Link>
          <span className="velova-mono-label opacity-60">§ Access</span>
        </div>

        <div className="flex-1 flex items-center justify-center p-5 sm:p-8 lg:p-16">
          <div className="w-full max-w-md">
            <div className="mb-8 sm:mb-10">
              <div className="velova-mono-label opacity-60 mb-4">Sign in</div>
              <h2 className="velova-display text-[32px] sm:text-[40px] lg:text-[44px] leading-[0.95]">
                Welcome{' '}
                <span className="velova-italic" style={{ color: 'var(--velova-blue-deep)' }}>
                  back
                </span>
                .
              </h2>
              <p className="text-[14px] opacity-70 mt-4">
                Email and password from your Velova account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="velova-block velova-block-rose p-4">
                  <div className="velova-mono-label mb-1">Error</div>
                  <div className="text-[13px] leading-tight">{error}</div>
                </div>
              )}

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="flex w-full items-center justify-center gap-3 rounded-full border border-[var(--velova-line-strong)] bg-white/80 px-5 py-3 text-[14px] font-semibold text-slate-800 transition hover:bg-white disabled:opacity-60 dark:bg-white/[0.04] dark:text-white dark:hover:bg-white/[0.06]"
              >
                {googleLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GoogleGlyph className="h-[18px] w-[18px]" />
                )}
                {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
              </button>

              <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.22em] opacity-50">
                <span className="h-px flex-1 bg-[var(--velova-line-strong)]" />
                or
                <span className="h-px flex-1 bg-[var(--velova-line-strong)]" />
              </div>

              <FieldEditorial
                id="email"
                label="Work email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={setEmail}
                disabled={loading}
                index="01"
                autoComplete="email"
              />

              <FieldEditorial
                id="password"
                label="Password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={setPassword}
                disabled={loading}
                index="02"
                autoComplete="current-password"
              />

              <button
                type="submit"
                disabled={loading}
                className="velova-btn velova-btn-ink w-full justify-center disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-[14px] h-[14px] animate-spin" />
                    Signing in
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowUpRight className="w-[14px] h-[14px]" />
                  </>
                )}
              </button>

              <div className="velova-hr" />

              <div className="flex items-center justify-between text-[13px]">
                <Link href="/forgot-password" className="opacity-70 hover:opacity-100 transition-opacity">
                  Forgot password?
                </Link>
                <div className="flex items-center gap-1.5">
                  <span className="opacity-70">New to Velova?</span>
                  <Link
                    href="/signup"
                    className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                  >
                    Create an account
                    <ArrowUpRight className="w-[12px] h-[12px]" />
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="p-5 sm:p-8 flex items-center justify-between text-[11px] opacity-50">
          <span>{SITE_META.copyright}</span>
          <span className="velova-mono-label">v1.0</span>
        </div>
      </section>
    </main>
  )
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  )
}

function FieldEditorial({
  id,
  label,
  type,
  placeholder,
  value,
  onChange,
  disabled,
  index,
  autoComplete,
}: {
  id: string
  label: string
  type: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  index: string
  autoComplete?: string
}) {
  const [showPassword, setShowPassword] = useState(false)
  const isPassword = type === 'password'
  const inputType = isPassword && showPassword ? 'text' : type

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="velova-mono-label opacity-70">
          {label}
        </label>
        <span className="velova-mono-label opacity-40">§ {index}</span>
      </div>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          disabled={disabled}
          autoComplete={autoComplete}
          className={`w-full bg-transparent border-0 border-b border-[var(--velova-line-strong)] pb-3 pt-1 text-[18px] focus:outline-none focus:border-[var(--velova-blue-deep)] transition-colors placeholder:opacity-40 ${isPassword ? 'pr-8' : ''}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            disabled={disabled}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            tabIndex={-1}
            className="absolute right-0 bottom-2 flex items-center justify-center w-7 h-7 opacity-60 hover:opacity-100 transition-opacity disabled:opacity-30"
          >
            {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
          </button>
        )}
      </div>
    </div>
  )
}
